import { Box, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const CARD_WIDTH = 260;

const CategorySlide = ({ allCategories = [] }) => {
  const navigate = useNavigate();
  const sliderRef = useRef(null);
  const isDown = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);
  const [isDragging, setIsDragging] = useState(false);

  const items = allCategories.map((item) => ({
    id: item.id,
    slug: item?.slug || "",
    image: item?.categoryImages?.[0]?.Image || "/images/placeholder.png",
    title: item?.categoryLocales?.[0]?.name || "Category",
    count: item?.productCount ?? 0,
  }));

  const loopItems = [...items, ...items];

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    const handleScroll = () => {
      if (slider.scrollLeft >= slider.scrollWidth / 2) {
        slider.scrollLeft = 0;
      }
      if (slider.scrollLeft <= 0) {
        slider.scrollLeft = slider.scrollWidth / 2;
      }
    };

    slider.addEventListener("scroll", handleScroll);
    return () => slider.removeEventListener("scroll", handleScroll);
  }, []);

  const handleCategoryClick = (slug, id) => {
    if (!isDragging && slug && id) {
      navigate(`/productlist/add-ons/${slug}/${id}`);
    }
  };

  // 🖱️ Grab Scroll Handlers
  const handleMouseDown = (e) => {
    isDown.current = true;
    setIsDragging(false);
    sliderRef.current.style.cursor = "grabbing";
    startX.current = e.pageX - sliderRef.current.offsetLeft;
    scrollLeft.current = sliderRef.current.scrollLeft;
  };

  const handleMouseLeave = () => {
    isDown.current = false;
    sliderRef.current.style.cursor = "grab";
  };

  const handleMouseUp = () => {
    isDown.current = false;
    sliderRef.current.style.cursor = "grab";
  };

  const handleMouseMove = (e) => {
    if (!isDown.current) return;
    e.preventDefault();
    setIsDragging(true);
    const x = e.pageX - sliderRef.current.offsetLeft;
    const walk = (x - startX.current) * 1.5; // speed
    sliderRef.current.scrollLeft = scrollLeft.current - walk;
  };

  return (
    <Box sx={{ width: "100%", overflow: "hidden" }}>
      <Box
        ref={sliderRef}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        sx={{
          display: "flex",
          gap: "5%",
          overflowX: "hidden",
          cursor: "grab",
          userSelect: "none",
        }}
      >
        {loopItems.map((item, index) => (
          <Box
            key={`${item.id}-${index}`}
            onClick={() => handleCategoryClick(item.slug, item.id)}
            sx={{
              minWidth: 240,
              height: 300,
              borderRadius: "18px",
              overflow: "hidden",
              position: "relative",
              backgroundColor: "#f5f5f5",
              flexShrink: 0,
              cursor: "pointer",
              transition: "transform 0.3s ease, box-shadow 0.3s ease",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 8px 24px rgba(119, 18, 55, 0.15)",
              },
              "&:active": {
                transform: isDragging ? "none" : "translateY(-2px)",
              },
            }}
          >
            <Box
              component="img"
              src={item.image}
              alt={item.title}
              sx={{ width: "100%", height: "100%", objectFit: "cover", pointerEvents: "none" }}
              draggable={false}
            />

            <Box
              sx={{
                position: "absolute",
                top: 12,
                left: 12,
                width:"100%",
                padding: "6px 10px",
                borderRadius: "8px",
                maxWidth: "80%",
                pointerEvents: "none",
              }}
            >
              <Typography
                sx={{
                  color: "#fff",
                  width:"100%",
                  display: "flex",
                  justifyContent: "start",
                  fontFamily: "Unbounded, sans-serif !important",
                  fontSize: "20px !important",
                  fontStyle: "normal",
                  fontWeight: 500,
                  lineHeight: "36px",
                  whiteSpace: "nowrap",
                }}
              >
                {item.title}
              </Typography>
            </Box>

            {item.count > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 12,
                  left: 12,
                  backgroundColor: "#fff",
                  padding: "2px 10px",
                  borderRadius: "999px",
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#771237",
                  pointerEvents: "none",
                }}
              >
                {item.count} Cake
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default CategorySlide;
